// hooks/usePaypalSubscription.js
// Handles the two-step PayPal app subscription flow:
// Step 1: POST /api/paypal/app/paypalsubscription  → get approvalUrl → redirect user to PayPal
// Step 2: GET  /api/paypal/app/payment-success     → called after PayPal redirects back → fetch plan info

import { useState } from "react";
import axios from "../api/axios";

// axios instance already has baseURL set to REACT_APP_API_URL (e.g. http://localhost:4040/api)
// so we call relative paths (no extra /api prefix) to avoid doubling the /api segment.

/**
 * usePaypalSubscription
 *
 * Usage inside any component:
 *   const { initiateSubscription, fetchPaymentResult, isLoading, error } = usePaypalSubscription();
 */
const usePaypalSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Step 1 — Initiate a PayPal subscription.
   *
   * Sends user + plan details to the backend, receives a PayPal approval URL,
   * then redirects the browser to PayPal so the user can approve the subscription.
   *
   * @param {Object} params
   * @param {number|string} params.UserId      - DB user id
   * @param {string}        params.FirstName
   * @param {string}        params.LastName
   * @param {string}        params.Email
   * @param {string}        params.Amount      - "14.99" | "29.99" | "99.99"
   * @param {string}        params.Version     - product version label, used as itemName
   * @param {string}        params.PlanId      - PayPal Plan ID (P-xxx…)
   * @param {string}       [params.ReturnUrl]  - where PayPal should redirect after approval
   *                                            defaults to <origin>/payment-success
   * @param {string}       [params.CancelUrl]  - where PayPal should redirect on cancel
   *                                            defaults to <origin>/payment-cancel
   */
  const initiateSubscription = async ({
    UserId,
    FirstName,
    LastName,
    Email,
    Amount,
    Version,
    PlanId,
    ReturnUrl,
    CancelUrl,
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        UserId,
        FirstName,
        LastName,
        Email,
        Amount,
        Version,
        PlanId,
        // Default return / cancel URLs point back to this app
        ReturnUrl: ReturnUrl || `${window.location.origin}/payment-success`,
        CancelUrl: CancelUrl || `${window.location.origin}/payment-cancel`,
      };

      const { data } = await axios.post(
        `/paypal/app/paypalsubscription`,
        payload
      );

      if (!data?.approvalUrl) {
        throw new Error("No approval URL returned from server.");
      }

      // Redirect the user to PayPal for approval.
      // PayPal will redirect back to ReturnUrl with ?token=<subscriptionId> appended.
      console.log("data?.approvalUrl :",data?.approvalUrl);
      window.location.href = data.approvalUrl;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err.message ||
        "Failed to initiate PayPal subscription.";
      setError(message);
      setIsLoading(false); // only reset here; on success the page navigates away
    }
  };

  /**
   * Step 2 — Fetch the subscription result after PayPal redirects the user back.
   *
   * Call this on the /payment-success page. PayPal appends ?token=<subscriptionId>
   * to the return URL. Pass that token here.
   *
   * @param {string} token - the subscription ID from the URL query param (?token=xxx)
   * @returns {Object|null} subscription info or null on failure
   *
   * Return shape (mirrors AppPaymentSuccess response):
   * {
   *   success, subscriptionTransactionId, userId, email,
   *   planValidity,   // 1 = monthly | 3 = quarterly | 12 = annual
   *   expiryDate,     // "YYYY-MM-DD"
   *   createdDate,    // "YYYY-MM-DD"
   *   subscriptionStatus, paymentStatus, amount, buyer, version
   * }
   */
  const fetchPaymentResult = async (token) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use a path relative to the axios baseURL (which already includes /api)
      const { data } = await axios.get(`/paypal/app/payment-success`, {
        params: { token },
      });

      // 202 means PayPal fired the webhook but the DB record isn't written yet.
      // The component should poll again after a short delay.
      if (data?.message?.includes("pending")) {
        return { pending: true, subscriptionTransactionId: data.subscriptionTransactionId };
      }

      return data; // { success: true, userId, planValidity, expiryDate, … }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err.message ||
        "Failed to fetch payment result.";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { initiateSubscription, fetchPaymentResult, isLoading, error };
};

export default usePaypalSubscription;